import { every } from 'lodash-es';
import { useContext, useEffect, useRef, type FocusEvent } from 'react';

import { XYChartSynchronizedCrosshair } from './XYChartSynchronizedCrosshair.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { InteractionsOverlay } from '../../../core/components/interactions-overlay/InteractionsOverlay.js';
import { useOverlay } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { reduceExploreCancelHighlight } from '../../store/reducer-explorer.js';
import {
  useInteractionModeState,
  useSelectState,
  useSetState,
  useZoomState,
} from '../../store/selectors.js';
import { XYChartSelectionArea } from '../selection/XYChartSelectionArea.js';
import { XYChartToolbar } from '../toolbar/XYChartToolbar.js';
import { XYChartOverlayTooltip } from '../tooltip/XYChartOverlayTooltip.js';
import { XYChartCrosshair } from './explore/XYChartCrosshair.js';
import { XYChartLinearPoint } from './explore/XYChartLinearPoint.js';
import { XYChartRectanglePoint } from './explore/XYChartRectanglePoint.js';
import { useOverlayEvents } from './hooks/useOverlayEvents.js';
import { isOwnEvent } from '../../../core/components/interactions-overlay/utils/is-own-event.js';
import {
  useSelector,
  useTooltipSnapshot,
} from '../../../core/components/overlay-tooltip/selectors.js';
import type { _SelectionRef } from '../../../core/components/selection-area/types.js';
import { SynchronizedSelectionArea } from '../../../core/components/shared-chart-interactions/components/SynchronizedSelectionArea.js';
import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { useToolbarHandlerRef } from '../../../core/components/toolbar/hooks/useToolbarHandlerRef.js';
import { ChartVariantContext } from '../../../core/context/ChartVariantContext.js';
import { useEffectOnValueChange } from '../../../core/hooks/useEffectOnValueChange.js';
import { zIndicesXYChartAreas } from '../../constants/xy-chart-z-index-areas.js';
import { XYChartSelectionAreaRefContext } from '../../context/XYChartSelectionAreaRef.context.js';
import { useSelectConfig } from '../../hooks/useSelectConfig.js';
import { useSyncedXScaleByPriority } from '../../hooks/useSyncedXScaleByPriority.js';
import { useXYChartCrosshairHidden } from '../../hooks/useXYChartCrosshairHidden.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartId } from '../../hooks/useXYChartId.js';

export const XYChartOverlay = () => {
  const setState = useSetState();
  const handlerRef = useToolbarHandlerRef();
  const overlay = useOverlay();
  const selectionRef = useRef<_SelectionRef>(null);
  const { width, height } = useGridPlotSize();
  useSelector(({ inBounds, pinned }) => `${inBounds}-${pinned}`);
  const { inBounds, pinned } = useTooltipSnapshot();
  const {
    handleModeChange,
    handleHover,
    handleTogglePin,
    handleReset,
    handleLeave,
    handlePanChange,
    handleSelectChange,
    handleSelectEnd,
    handleZoomChange,
    handleSelectStart,
    resetTooltip,
  } = useOverlayEvents();
  const mode = useInteractionModeState();
  const selectState = useSelectState();
  const synchronizedSelection = useSynchronizedSelection();
  const chartId = useXYChartId();
  const syncedXScale = useSyncedXScaleByPriority();
  const { axesMetadata } = useXYChartData();
  const { crosshair: crosshairConfig } = useContext(ChartVariantContext);
  const { zoomOutEnabled } = useZoomState();
  const isZoomDisabled = !axesMetadata.isZoomEnabled;
  const isPanDisabled = !(axesMetadata.isPanEnabled && zoomOutEnabled);
  const isSelectDisabled = useSelectConfig()?.disabled === true;

  const { hideHorizontal, hideVertical } = useXYChartCrosshairHidden();

  useEffect(() => {
    if (
      every([
        selectState,
        !synchronizedSelection.selectedChartId,
        !synchronizedSelection?.disabled,
      ])
    ) {
      // clear current chart selection if the shared selection was removed
      setState((s) => ({ ...s, select: undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synchronizedSelection.selectedChartId]);

  useEffect(() => {
    if (synchronizedSelection.selectedChartId === chartId && !selectState) {
      // clear shared chart selection if the current selection was removed
      synchronizedSelection.resetSelectionState?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!selectState]);

  useEffectOnValueChange(() => {
    if (!pinned) {
      setState(reduceExploreCancelHighlight);
    }
  }, [pinned, setState]);

  const showHighlightedData =
    selectState === undefined || selectState?.variant === 'select';
  const handleFocus = (event?: FocusEvent) => {
    if (!event || event.cancelable) {
      return;
    }
    if (isOwnEvent(event)) {
      handlerRef.current.handleFocus();
    }
  };
  return (
    <InteractionsOverlay
      style={{
        zIndex: zIndicesXYChartAreas.overlays,
        userSelect: selectState?.finished === false ? 'none' : 'auto',
        cursor:
          hideHorizontal && hideVertical ? 'default' : crosshairConfig?.cursor,
      }}
      ref={overlay.overlayRef}
      mode={mode}
      pinned={pinned}
      selectionArea={
        selectState ? [selectState?.start, selectState?.end] : undefined
      }
      width={width}
      height={height}
      onModeChange={handleModeChange}
      onReset={handleReset}
      onLeave={handleLeave}
      onPanChange={handlePanChange}
      onHover={handleHover}
      onFocus={handleFocus}
      onTogglePin={({ pin, position }) => handleTogglePin(pin, position)}
      onSelectStart={handleSelectStart}
      onSelectChange={handleSelectChange}
      onSelectEnd={(event) => handleSelectEnd(event, selectionRef)}
      onZoomChange={handleZoomChange}
      isZoomDisabled={isZoomDisabled}
      isZoomXDisabled={isZoomDisabled}
      isPanDisabled={isPanDisabled}
      isSelectDisabled={isSelectDisabled}
    >
      {(inBounds || pinned || selectState) && (
        <>
          <XYChartOverlayTooltip />
          <XYChartCrosshair chartRef={overlay.overlayRef} />
          {showHighlightedData && (
            <>
              <XYChartRectanglePoint />
              <XYChartLinearPoint />
            </>
          )}
        </>
      )}
      <XYChartSynchronizedCrosshair />
      <XYChartSelectionAreaRefContext.Provider value={selectionRef}>
        <XYChartToolbar onMouseEnter={resetTooltip} handlerRef={handlerRef} />
        <XYChartSelectionArea />
      </XYChartSelectionAreaRefContext.Provider>
      {synchronizedSelection.selectedChartId &&
        synchronizedSelection.selectedChartId !== chartId &&
        syncedXScale && <SynchronizedSelectionArea scale={syncedXScale} />}
    </InteractionsOverlay>
  );
};

XYChartOverlay['displayName'] = 'XYChartOverlay';
